'use client';

import { useState } from 'react';
import { ArrowRight, Check } from 'lucide-react';
import AnimateOnScroll from '@/components/AnimateOnScroll';
import { Button, Card, CheckItem } from '@/components/sections/kit';
import BillingToggle from './BillingToggle';
import GridSection from '@/components/site/GridSection';
import GridRule from '@/components/site/GridRule';
import { routePath } from '@/lib/routes';
import { CURRENCY, TIERS, type Billing, type Tier } from './data';

/** Hero line in an overflow-hidden mask; the descender padding keeps letters
 *  like "g" from clipping while the line rises (see .pricing-hero-line). */
function HeroLine({ index, children }: { index: number; children: React.ReactNode }) {
  return (
    <span className="block overflow-hidden pb-[0.06em] -mb-[0.06em]">
      <span
        className="pricing-hero-line block"
        style={{ animationDelay: `${120 + index * 110}ms` }}
      >
        {children}
      </span>
    </span>
  );
}

function TierCard({ tier, billing }: { tier: Tier; billing: Billing }) {
  const price = tier.price ? tier.price[billing] : null;

  return (
    <Card
      shadow
      className={`relative flex h-full flex-col p-8 transition-[transform,box-shadow,border-color] duration-300 ease-out hover:-translate-y-1 ${
        tier.highlight
          ? 'border-brand-600/40 shadow-ref-glow tier-glow-breathe'
          : 'hover:border-line-3'
      }`}
    >
      {tier.highlight && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-brand-600 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.08em] text-white shadow-[0_10px_24px_-10px_rgba(215,14,98,0.55)]">
          Most popular
        </span>
      )}

      <h3 className="text-lg font-bold text-ink">{tier.name}</h3>
      <p className="mt-1 text-sm text-muted">{tier.tagline}</p>

      <div className="mt-6 flex items-baseline gap-1">
        {price !== null ? (
          <>
            {/* Remount on billing flip → pop swap animation. */}
            <span
              key={billing}
              className="animate-price-pop font-onest text-5xl font-extrabold tracking-tight text-ink tabular-nums motion-reduce:animate-none"
            >
              {CURRENCY}
              {price}
            </span>
            <span className="text-muted">/mo</span>
          </>
        ) : (
          <span className="font-onest text-5xl font-extrabold tracking-tight text-ink">Custom</span>
        )}
      </div>
      {/* Fixed height so the card never resizes when the toggle flips. */}
      <p className="mt-2 h-5 text-[13px] text-muted">{tier.hint[billing]}</p>

      <Button
        variant={tier.cta.variant}
        href={tier.cta.href}
        className="mt-6 w-full justify-center"
      >
        {tier.cta.label}
      </Button>

      <ul className="mt-8 flex-1 space-y-3">
        {tier.features.map((feat) => (
          <li key={feat}>
            <CheckItem className="text-sm">{feat}</CheckItem>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/** Plan terms visible on-page (anti-pattern rail: never hide them). Premium
 *  is assigned by our team today — no checkout, so no proration or
 *  cancel-anytime promise (see SELF_SERVE_BILLING in ./data). The ownership item links to the /custom-domains ownership band. */
function TrustStrip() {
  const items: React.ReactNode[] = [
    'No credit card for Free',
    'Premium set up by our team — no lock-in',
    <>
      Your domain stays yours —{' '}
      <a
        href={`${routePath('customDomains')}#ref-ownership`}
        className="text-brand-600 hover:text-brand-700"
      >
        re-point it anytime
      </a>
    </>,
  ];

  return (
    <div className="mt-[30px] flex flex-wrap items-center justify-center gap-x-8 gap-y-3 rounded-ref border border-line bg-surface px-5 py-4">
      {items.map((item, i) => (
        <span key={i} className="flex items-center gap-[9px] text-[13.5px] text-muted">
          <Check
            size={14}
            className="check-draw shrink-0 text-brand-600"
            style={{ '--check-delay': `${200 + i * 80}ms` } as React.CSSProperties}
            aria-hidden
          />
          {item}
        </span>
      ))}
    </div>
  );
}

/** Hero (oversized masked heading + billing toggle) followed by the three
 *  data-driven tier cards and the trust strip. Owns the billing state shared
 *  by toggle and cards. The page-wide grid glow behind everything lives in
 *  GridGlow (mounted in app/[lang]/pricing/page.tsx). */
export default function PricingTiersSection() {
  const [billing, setBilling] = useState<Billing>('monthly');

  return (
    <>
      {/* Top padding clears the fixed one-row navbar plus breathing room.
          Editorial hero: mono eyebrow, oversized left-aligned title (line-mask
          reveal), then an info row (lead → arrow → reassurance, billing toggle
          right), then a hairline. */}
      <GridSection
        id="plans"
        className="pt-[calc(var(--grid-cell,72px)*2.5)] pb-[64px]"
      >
        <span className="block font-jetbrains text-[11px] font-semibold tracking-[0.16em] text-brand-600">
          PRICING &middot; PLANS &amp; BILLING
        </span>
        <h1 className="mt-5 font-onest text-[clamp(48px,7vw,104px)] font-extrabold leading-[0.98] tracking-[-0.045em] text-ink">
          <HeroLine index={0}>Simple pricing,</HeroLine>
          <HeroLine index={1}>
            smart links<span className="pricing-hero-dot text-brand-600">.</span>
          </HeroLine>
        </h1>

        <AnimateOnScroll animation="fade-up" delay={120}>
          <div className="mt-12 flex flex-col gap-7 md:flex-row md:items-center md:gap-8">
            <p className="max-w-[440px] text-[16.5px] leading-[1.55] text-muted">
              Start free. Upgrade when you need smart routing, custom domains
              and deeper analytics.
            </p>
            <ArrowRight size={22} className="hidden shrink-0 text-brand-600 md:block" />
            <p className="max-w-[240px] text-[16.5px] leading-[1.55] text-muted">
              No credit card required. Premium on request.
            </p>
            <div className="md:ml-auto">
              <BillingToggle value={billing} onChange={setBilling} />
            </div>
          </div>
          <GridRule />
        </AnimateOnScroll>
      </GridSection>

      <GridSection className="pb-[110px]">
        <AnimateOnScroll animation="fade-up" delay={60}>
          {/* pt-3 keeps the overhanging "Most popular" badge inside the block. */}
          <div data-grid-block className="grid items-stretch gap-6 pt-3 md:grid-cols-3">
            {TIERS.map((tier) => (
              <TierCard key={tier.id} tier={tier} billing={billing} />
            ))}
          </div>
        </AnimateOnScroll>

        <AnimateOnScroll animation="fade-up" delay={140}>
          <div data-grid-block>
            <TrustStrip />
          </div>
        </AnimateOnScroll>
      </GridSection>
    </>
  );
}
